import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  inject,
  SimpleChanges,
} from '@angular/core';
import { ArableEnvironment } from '@next/arable-lib/shared';
import { EnvironmentInjector } from '@next/core-lib/environment';
import type { Feature as GeoJSONFeature } from 'geojson';
import { Map, View } from 'ol';
import { defaults as defaultControls } from 'ol/control';
import { getCenter } from 'ol/extent';
import Feature from 'ol/Feature';
import { GeoJSON as GeoJSONFormat } from 'ol/format';
import { Geometry, Point, Polygon } from 'ol/geom';
import { defaults as defaultInteractions } from 'ol/interaction';
import { Tile, Vector as VectorLayer } from 'ol/layer';
import { fromLonLat } from 'ol/proj';
import { XYZ, Vector as VectorSource } from 'ol/source';
import { Fill, Stroke, Style, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';

import { FmsDiagnoseFarm, FmsDiagnoseLinkedField } from '../../../shared/models/fms-diagnose.model';

export type FmsDiagnoseMapLayer = 'linked' | 'agrifirm-only' | 'bms-only' | 'bms-overlap';

export type FmsDiagnoseMapField = FmsDiagnoseFarm;

export interface FmsDiagnoseMapFeature {
  field: FmsDiagnoseMapField;
  layer: FmsDiagnoseMapLayer;
  geometry: Geometry;
}

export const FMS_DIAGNOSE_MAP_COLORS: Record<
  FmsDiagnoseMapLayer,
  { fill: string; stroke: string; lineDash?: number[]; width: number }
> = {
  linked: { fill: 'rgba(0, 158, 115, 0.18)', stroke: '#009e73', width: 4 },
  'agrifirm-only': { fill: 'rgba(230, 159, 0, 0.18)', stroke: '#e69f00', width: 3 },
  'bms-only': { fill: 'rgba(0, 114, 178, 0.18)', stroke: '#0072b2', width: 3 },
  'bms-overlap': {
    fill: 'rgba(0, 0, 0, 0)',
    stroke: '#111827',
    lineDash: [5, 6],
    width: 3,
  },
};

const defaultMapCenter: [number, number] = [5.725022406992621, 51.48995668637979];
const geoJsonFormat = new GeoJSONFormat();

function readGeoJsonGeometry(feature: GeoJSONFeature | null | undefined) {
  if (!feature) {
    return undefined;
  }

  try {
    return geoJsonFormat
      .readFeature(feature, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      })
      .getGeometry();
  } catch {
    return undefined;
  }
}

function addFeature(
  features: FmsDiagnoseMapFeature[],
  field: FmsDiagnoseMapField,
  layer: FmsDiagnoseMapLayer,
  feature: GeoJSONFeature | null,
): void {
  const geometry = readGeoJsonGeometry(feature);
  if (geometry) {
    features.push({ field, layer, geometry });
  }
}

export function buildFmsDiagnoseMapFeatures(
  internalOnlyFields: FmsDiagnoseFarm[],
  linkedFields: FmsDiagnoseLinkedField[],
  providerOnlyFields: FmsDiagnoseFarm[],
): FmsDiagnoseMapFeature[] {
  const features: FmsDiagnoseMapFeature[] = [];

  internalOnlyFields.forEach((field) =>
    addFeature(features, field, 'agrifirm-only', field.feature),
  );

  linkedFields.forEach((field) => {
    addFeature(features, field.internalFarm, 'linked', field.internalFarm.feature);
    addFeature(features, field.providerFarm, 'bms-overlap', field.providerFarm.feature);
  });

  providerOnlyFields.forEach((field) => addFeature(features, field, 'bms-only', field.feature));

  return features;
}

@Component({
  selector: 'app-fms-diagnose-map',
  templateUrl: './fms-diagnose-map.component.html',
  styleUrls: ['./fms-diagnose-map.component.scss'],
  standalone: false,
})
export class FmsDiagnoseMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() internalOnlyFields: FmsDiagnoseFarm[] = [];
  @Input() linkedFields: FmsDiagnoseLinkedField[] = [];
  @Input() providerOnlyFields: FmsDiagnoseFarm[] = [];
  @Input() ariaLabel = 'FMS crop fields map';

  @ViewChild('mapTarget', { static: true }) private readonly mapTarget!: ElementRef<HTMLDivElement>;

  private readonly environment = inject<ArableEnvironment>(EnvironmentInjector);
  private readonly fieldSource = new VectorSource();
  private readonly labelSource = new VectorSource();
  private map?: Map;
  private baseLayer?: Tile;
  private satelliteLayer?: Tile;
  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;
  mapType: 'map' | 'satellite' = 'map';

  ngAfterViewInit(): void {
    this.renderFeatures();

    const baseLayer = this.createBaseLayer();
    const satelliteLayer = this.createSatelliteLayer();
    satelliteLayer.setVisible(false);
    this.baseLayer = baseLayer;
    this.satelliteLayer = satelliteLayer;

    this.map = new Map({
      target: this.mapTarget.nativeElement,
      controls: defaultControls({
        attribution: false,
        rotate: false,
        zoom: false,
      }),
      interactions: defaultInteractions(),
      layers: [
        baseLayer,
        satelliteLayer,
        new VectorLayer({
          source: this.fieldSource,
          style: (feature) => this.fieldStyle(feature as Feature),
          zIndex: 10,
        }),
        new VectorLayer({
          source: this.labelSource,
          style: (feature) => this.labelStyle(feature as Feature),
          zIndex: 11,
        }),
      ],
      view: new View({
        center: fromLonLat(defaultMapCenter),
        projection: 'EPSG:3857',
        zoom: 15,
        maxZoom: 20,
      }),
    });

    this.refreshMap();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.refreshMap());
      this.resizeObserver.observe(this.mapTarget.nativeElement);
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.refreshMap();
        }
      });
      this.intersectionObserver.observe(this.mapTarget.nativeElement);
    }
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.map) {
      return;
    }

    this.renderFeatures();
    this.refreshMap();
  }

  centerMap(): void {
    this.refreshMap();
  }

  switchView(): void {
    this.mapType = this.mapType === 'map' ? 'satellite' : 'map';
    this.baseLayer?.setVisible(this.mapType === 'map');
    this.satelliteLayer?.setVisible(this.mapType === 'satellite');
  }

  zoomIn(): void {
    const view = this.map?.getView();
    const zoom = view?.getZoom();
    if (view && zoom !== undefined) {
      view.setZoom(Math.min(zoom + 1, view.getMaxZoom()));
    }
  }

  zoomOut(): void {
    const view = this.map?.getView();
    const zoom = view?.getZoom();
    if (view && zoom !== undefined) {
      view.setZoom(Math.max(zoom - 1, view.getMinZoom()));
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.map?.setTarget(undefined);
    this.fieldSource.clear();
    this.labelSource.clear();
  }

  private createBaseLayer(): Tile {
    return new Tile({
      source: new XYZ({
        url: [
          'https://atlas.microsoft.com/map/tile',
          '?api-version=2024-04-01&tilesetId=microsoft.base.road&tileSize=512',
          '&zoom={z}&x={x}&y={y}',
          `&subscription-key=${this.environment.AZURE_MAPS_SUBSCRIPTION_KEY}`,
        ].join(''),
        tileSize: 512,
      }),
      zIndex: 0,
    });
  }

  private createSatelliteLayer(): Tile {
    return new Tile({
      source: new XYZ({
        url: [
          'https://atlas.microsoft.com/map/tile',
          '?api-version=2024-04-01&tilesetId=microsoft.imagery&tileSize=512',
          '&zoom={z}&x={x}&y={y}',
          `&subscription-key=${this.environment.AZURE_MAPS_SATELLITE_SUBSCRIPTION_KEY}`,
        ].join(''),
        tileSize: 512,
      }),
      zIndex: 0,
    });
  }

  private renderFeatures(): void {
    this.fieldSource.clear();
    this.labelSource.clear();

    const features = buildFmsDiagnoseMapFeatures(
      this.internalOnlyFields,
      this.linkedFields,
      this.providerOnlyFields,
    );

    features.forEach((mapFeature) => {
      const feature = new Feature({ geometry: mapFeature.geometry });
      feature.set('layer', mapFeature.layer);
      feature.set('field', mapFeature.field);
      this.fieldSource.addFeature(feature);

      if (mapFeature.layer !== 'bms-overlap') {
        const labelCoordinate =
          mapFeature.geometry instanceof Polygon
            ? mapFeature.geometry.getInteriorPoint().getCoordinates()
            : mapFeature.geometry.getClosestPoint(getCenter(mapFeature.geometry.getExtent()));
        const label = new Feature({
          geometry: new Point(labelCoordinate),
        });
        label.set('field', mapFeature.field);
        label.set('layer', mapFeature.layer);
        this.labelSource.addFeature(label);
      }
    });
  }

  private refreshMap(): void {
    if (!this.map) {
      return;
    }

    this.map.updateSize();
    const extent = this.fieldSource.getExtent();
    if (extent.every(Number.isFinite)) {
      this.map.getView().fit(extent, {
        duration: 0,
        maxZoom: 17,
        padding: [36, 36, 36, 36],
      });
    }
    this.map.renderSync();
  }

  private fieldStyle(feature: Feature): Style {
    const layer = feature.get('layer') as FmsDiagnoseMapLayer;
    const colors = FMS_DIAGNOSE_MAP_COLORS[layer];

    return new Style({
      fill: new Fill({ color: colors.fill }),
      image: new CircleStyle({
        fill: new Fill({ color: colors.fill }),
        radius: 6,
        stroke: new Stroke({ color: colors.stroke, width: colors.width }),
      }),
      stroke: new Stroke({
        color: colors.stroke,
        lineDash: colors.lineDash,
        width: colors.width,
      }),
    });
  }

  private labelStyle(feature: Feature): Style {
    const field = feature.get('field') as FmsDiagnoseMapField;

    return new Style({
      text: new Text({
        backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.65)' }),
        fill: new Fill({ color: '#ffffff' }),
        font: '700 12px sans-serif',
        padding: [3, 5, 3, 5],
        text: field.name?.trim() || '',
      }),
    });
  }
}
